<template>
  <AccessDeniedRoot :inert="loadingState === LoadingStates.Loading" :class="{ 'opacity-50': loadingState === LoadingStates.Loading }">
    <template #heading>{{ heading }}</template>

    <template #description>{{ description }}</template>

    <template #cta>
      <Button v-if="loadingState === LoadingStates.Idle" class="w-full" @click="debouncedRequest">Renew Access</Button>
    </template>
  </AccessDeniedRoot>
</template>

<script setup lang="ts">
import AccessDeniedRoot from '~/components/access/AccessDeniedRoot.vue'
import { useToast } from '~/components/ui/toast'
import type { FetchError } from 'ofetch'
import debounce from '~~/lib/debounce'

enum LoadingStates {
  Idle,
  Loading,
  Finished,
}
const loadingState = ref<LoadingStates>(LoadingStates.Idle)

const { requestRenewalByUUID } = useDeckPermissions()
const { toast } = useToast()

const heading = computed(() => {
  if (loadingState.value === LoadingStates.Finished) {
    return 'REQUEST SENT'
  }

  return 'ACCESS TO THIS PAGE HAS EXPIRED'
})

const description = computed(() => {
  if (loadingState.value === LoadingStates.Finished) {
    return "If you still have access, you'll receive an email with a new access link shortly."
  }

  return 'Your access has expired. Please request access to receive a new confidential link to view the presentation.'
})

async function requestAccessRenewal() {
  loadingState.value = LoadingStates.Loading
  const route = useRoute()
  const { auth } = route.query
  try {
    await requestRenewalByUUID(String(auth))
    loadingState.value = LoadingStates.Finished
  } catch (e) {
    toast({
      title: 'Unable to renew access',
      description: (e as FetchError).data,
      variant: 'destructive',
    })
    loadingState.value = LoadingStates.Idle
  }
}
const debouncedRequest = debounce(requestAccessRenewal, 1000)
</script>
